<template>
  <div class="x-video">
    <video
      v-bind="omit({ ...$attrs })"
      ref="video"
      controls="false"
      preload="auto"
      loading="lazy"
      :src="src"
      muted
      x5-video-player-type
      x5-video-player-fullscreen
      x-webkit-airplay
      webkit-playsinline
      playsinline
    >
      <slot />
    </video>
  </div>
</template>

<script lang="ts" setup>
import { omit } from 'lodash-es';

interface VideoHTMLAttributes {
  src?: string;
}

const video = ref<HTMLVideoElement>();

const props = defineProps<VideoHTMLAttributes>();

watch(
  () => props.src,
  src => {
    if (src) {
      checkHLSSupport();
    }
  },
);

async function patchHLS(source: string) {
  const Hls = await import('hls.js').then(m => m.default);
  if (
    video.value &&
    !video.value!.canPlayType('application/vnd.apple.mpegurl') &&
    Hls.isSupported()
  ) {
    const hls = new Hls();
    hls.loadSource(source);
    hls.attachMedia(video.value);
  }
}

function checkHLSSupport() {
  const src = props.src;

  if (src?.endsWith('.m3u8')) {
    patchHLS(src);
    return;
  }

  // source detect
  const sources = video.value?.getElementsByTagName('source');
  if (!sources) {
    return;
  }

  if (sources.length > 0) {
    for (const source of sources) {
      if (source.src.endsWith('.m3u8')) {
        patchHLS(source.src);
        break;
      }
    }
  }
}

const observer = ref<MutationObserver | null>(null);

onMounted(() => {
  observer.value = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        checkHLSSupport();
      }
    }
  });
  if (video.value) {
    observer.value.observe(video.value, { childList: true });
    checkHLSSupport();
  }
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
  observer.value = null;
});

defineExpose({
  video,
});
</script>

<style scoped lang="scss">
.x-video {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--back-5);
  box-shadow: 0px 0.533vw 2.8vw 0px rgba(0, 0, 0, 0.1);
  border-radius: 0.8vw;
  overflow: hidden;
  video {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
  .poster {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    object-fit: contain;
  }
}
</style>
